import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_BoxSizing() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📦 Advanced CSS: Box Sizing</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>box-sizing</code> property controls how the total width and
        height of an element are calculated. Understanding{" "}
        <code>content-box</code> and <code>border-box</code> is key to creating
        predictable layouts and responsive designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#dbeafe,#93c5fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e40af" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>content-box</code> calculates width/height excluding padding &
            border.
          </li>
          <li>
            <code>border-box</code> includes padding & border in width/height.
          </li>
          <li>
            Global resets using <code>* box-sizing: border-box </code> prevent
            layout issues.
          </li>
          <li>Box-sizing is crucial for responsive grids and card layouts.</li>
          <li>
            Combine with padding, margin, and border for precise spacing
            control.
          </li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">📝 Examples of Box Sizing</h2>

        {/* 1. Content-box vs Border-box */}
        <h3>1. Content-box vs Border-box</h3>
        <CodeCopyBlock
          code={`.content-box {
  width: 200px;
  padding: 20px;
  border: 5px solid #3b82f6;
  box-sizing: content-box;
}

.border-box {
  width: 200px;
  padding: 20px;
  border: 5px solid #9333ea;
  box-sizing: border-box;
}`}
        />
        <div style={{ display: "flex", gap: "20px", marginBottom: "1rem" }}>
          <div
            style={{
              width: "200px",
              padding: "20px",
              border: "5px solid #3b82f6",
              boxSizing: "content-box",
              background: "#dbeafe",
              textAlign: "center",
            }}
          >
            Content-box
          </div>
          <div
            style={{
              width: "200px",
              padding: "20px",
              border: "5px solid #9333ea",
              boxSizing: "border-box",
              background: "#ede9fe",
              textAlign: "center",
            }}
          >
            Border-box
          </div>
        </div>

        {/* 2. Global Box-sizing Reset */}
        <h3>2. Global Reset</h3>
        <CodeCopyBlock
          code={`*,
*::before,
*::after {
  box-sizing: border-box;
}`}
        />
        <p>
          Using a global reset prevents padding and borders from breaking
          layout, especially in grids or flex containers.
        </p>

        {/* 3. Box-sizing with Flex Layout */}
        <h3>3. Flex Layout Example</h3>
        <CodeCopyBlock
          code={`.flex-container {
  display: flex;
  gap: 10px;
}
.flex-item {
  width: 100px;
  padding: 20px;
  border: 5px solid #f59e0b;
  box-sizing: border-box;
}`}
        />
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "1rem",
          }}
        >
          <div
            style={{
              width: "100px",
              padding: "20px",
              border: "5px solid #f59e0b",
              boxSizing: "border-box",
              background: "#fef3c7",
              textAlign: "center",
            }}
          >
            Flex 1
          </div>
          <div
            style={{
              width: "100px",
              padding: "20px",
              border: "5px solid #f97316",
              boxSizing: "border-box",
              background: "#ffedd5",
              textAlign: "center",
            }}
          >
            Flex 2
          </div>
        </div>

        {/* 4. Responsive Card with Border-box */}
        <h3>4. Responsive Card</h3>
        <CodeCopyBlock
          code={`.card {
  width: 100%;
  max-width: 300px;
  padding: 20px;
  border: 2px solid #3b82f6;
  border-radius: 12px;
  box-sizing: border-box;
}`}
        />
        <div
          style={{
            width: "100%",
            maxWidth: "300px",
            padding: "20px",
            border: "2px solid #3b82f6",
            borderRadius: "12px",
            boxSizing: "border-box",
            background: "#dbeafe",
            marginBottom: "1rem",
          }}
        >
          Responsive Card
        </div>

        {/* 5. Interactive Toggle Example */}
        <h3>5. Interactive Padding Change</h3>
        <CodeCopyBlock
          code={`.box {
  width: 150px;
  padding: 10px;
  border: 3px solid #9333ea;
  box-sizing: border-box;
  transition: padding 0.3s ease;
}
.box:hover {
  padding: 30px;
}`}
        />
        <div
          style={{
            width: "150px",
            padding: "10px",
            border: "3px solid #9333ea",
            boxSizing: "border-box",
            transition: "padding 0.3s ease",
            background: "#ede9fe",
            textAlign: "center",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.padding = "30px")}
          onMouseLeave={(e) => (e.currentTarget.style.padding = "10px")}
        >
          Hover Me
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#dbeafe,#93c5fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e40af" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine box-sizing with grid and flex layouts for consistent
            spacing.
          </li>
          <li>
            Use box-sizing with media queries for responsive card and container
            sizes.
          </li>
          <li>
            Experiment with interactive elements to see the impact of
            content-box vs border-box.
          </li>
          <li>
            Combine with CSS variables for dynamic box-sizing effects if needed.
          </li>
        </ul>
      </div>
    </div>
  );
}
